import { useCallback, useMemo, useState } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { format, isValid, parseISO } from "date-fns"
import {
	IconFolderOutline18 as FolderIcon,
	IconTrash2Outline18 as Trash2Icon,
	IconArrowTurnLeftOutline18 as UndoIcon,
} from "nucleo-ui-outline-18"

import { Button, Dialog, IconButton, Select } from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import type { MarkdownArticle } from "../utils/markdown-parser"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { useImportPortalId } from "../../import-portal"

interface MarkdownPreviewDialogProps {
	isOpen: boolean
	onOpenChange: () => void
	articles: MarkdownArticle[]
	onSuccess: () => void
}

function parseDate(dateString: string): Date | null {
	const isoDate = parseISO(dateString)
	if (isValid(isoDate)) return isoDate
	const nativeDate = new Date(dateString)
	if (isValid(nativeDate)) return nativeDate
	return null
}

function stripHtml(html: string): string {
	return html
		.replace(/<[^>]*>/g, "")
		.replace(/&nbsp;/g, " ")
		.replace(/&amp;/g, "&")
		.replace(/&lt;/g, "<")
		.replace(/&gt;/g, ">")
		.replace(/&quot;/g, '"')
		.replace(/&#39;/g, "'")
		.replace(/\n{3,}/g, "\n\n")
		.trim()
}

const NO_GROUP_VALUE = "__none__"
const NEW_GROUP_PREFIX = "__new__:"

interface GroupSelectProps {
	value: string | null
	onChange: (groupName: string | null) => void
}

function GroupSelect({ value, onChange }: GroupSelectProps) {
	const [search, setSearch] = useState("")
	const [groups] = useQuery(queries.helpCenterArticlesWithGroups())

	const options = useMemo(() => {
		const base = [
			{ value: NO_GROUP_VALUE, label: "No group" },
			...groups.map((g) => ({ value: g.name, label: g.name })),
		]

		// Inject "Create '...'" option when search doesn't match any existing group
		const trimmed = search.trim()
		const alreadyExists = groups.some(
			(g) => g.name.toLowerCase() === trimmed.toLowerCase(),
		)
		if (trimmed && !alreadyExists) {
			base.push({
				value: `${NEW_GROUP_PREFIX}${trimmed}`,
				label: `Create "${trimmed}"`,
			})
		}

		return base
	}, [groups, search])

	const selectValue = value ?? NO_GROUP_VALUE

	const handleChange = useCallback(
		(val: string) => {
			if (val === NO_GROUP_VALUE) {
				onChange(null)
			} else if (val.startsWith(NEW_GROUP_PREFIX)) {
				onChange(val.slice(NEW_GROUP_PREFIX.length))
			} else {
				onChange(val)
			}
			setSearch("")
		},
		[onChange],
	)

	return (
		<Select
			name="group-select"
			searchable
			searchPlaceholder="Search or type new group..."
			options={options}
			value={selectValue}
			onChange={handleChange}
			placeholder="Select or create group"
			Icon={FolderIcon}
		/>
	)
}

export function MarkdownPreviewDialog({
	isOpen,
	onOpenChange,
	articles,
	onSuccess,
}: MarkdownPreviewDialogProps) {
	const [deleted, setDeleted] = useState<number[]>([])
	const [overrideGroup, setOverrideGroup] = useState<string | null>(null)

	// Only show the group picker when none of the articles have a group assigned
	const noneHaveGroup = useMemo(
		() => articles.every((a) => !a.groupName),
		[articles],
	)

	const portalInstanceId = useImportPortalId()

	const bulkCreateArticlesMutation =
		api.workspace.bulkCreateArticles.useMutation({ onSuccess })

	const handleDelete = useCallback((i: number) => {
		setDeleted((prev) =>
			prev.includes(i) ? prev.filter((it) => it !== i) : [...prev, i],
		)
	}, [])

	const handleUpload = useCallback(() => {
		const uploadArticles = articles
			.filter((_, i) => !deleted.includes(i))
			.map((article) => ({
				title: article.title,
				content: article.content,
				summary: article.summary,
				imageUrl: article.imageUrl,
				groupName: noneHaveGroup
					? (overrideGroup ?? undefined)
					: article.groupName,
				published: article.published,
				dateIso: article.dateIso,
				icon: article.icon,
			}))

		analytics.track("upload-articles-markdown")
		bulkCreateArticlesMutation.mutate({
			articles: uploadArticles,
			portalInstanceId,
		})
	}, [
		articles,
		deleted,
		bulkCreateArticlesMutation,
		noneHaveGroup,
		overrideGroup,
		portalInstanceId,
	])

	const visibleCount = articles.length - deleted.length

	return (
		<Dialog
			title="Preview Markdown Articles"
			description="Please remove articles you might not want to upload"
			isOpen={isOpen}
			onOpenChange={onOpenChange}
			footer={
				<Button
					isLoading={bulkCreateArticlesMutation.isLoading}
					onClick={handleUpload}
				>
					Upload {visibleCount} Article{visibleCount !== 1 ? "s" : ""}
				</Button>
			}
		>
			<div className="space-y-3">
				{noneHaveGroup && (
					<div className="flex items-center gap-3">
						<span className="shrink-0 text-sm text-primary">
							Assign all to group:
						</span>
						<div className="flex-1">
							<GroupSelect value={overrideGroup} onChange={setOverrideGroup} />
						</div>
					</div>
				)}

				<div className="space-y-2">
					{articles.map((article, i) => (
						<div
							key={`markdown-article-preview-${i}`}
							className="relative flex flex-col overflow-hidden rounded-lg border p-2.5 text-sm text-secondary"
						>
							<div className="flex items-start justify-between border-b pb-2">
								<div className="flex-1">
									<div className="line-clamp-1 font-medium text-primary">
										{article.title}
									</div>
									<div className="line-clamp-1 text-xs text-gray-500">
										{article.filename}
									</div>
									{(article.groupName ??
										(noneHaveGroup ? overrideGroup : null)) && (
										<div className="mt-1 flex items-center gap-1 text-xs text-green-600">
											<FolderIcon className="h-3 w-3" />
											{article.groupName ?? overrideGroup}
										</div>
									)}
									{article.dateIso &&
										(() => {
											const parsedDate = parseDate(article.dateIso)
											return parsedDate ? (
												<span className="text-xs">
													{format(parsedDate, "MMM d, yyyy")}
												</span>
											) : (
												<span className="text-xs text-red-600">
													Invalid date: {article.dateIso}
												</span>
											)
										})()}
									{article.published !== undefined && (
										<span
											className={`ml-2 text-xs ${article.published ? "text-green-600" : "text-gray-500"}`}
										>
											{article.published ? "Published" : "Unpublished"}
										</span>
									)}
								</div>
								<IconButton
									onClick={() => handleDelete(i)}
									variant="ghost"
									Icon={deleted.includes(i) ? UndoIcon : Trash2Icon}
								/>
							</div>
							{article.summary && (
								<div className="mt-2 line-clamp-2 truncate text-xs text-tertiary">
									<strong>Summary:</strong> {article.summary}
								</div>
							)}
							{article.content && (
								<div className="mt-2 line-clamp-3">
									{stripHtml(article.content)}
								</div>
							)}
							{article.imageUrl && (
								<div className="mt-1 text-xs text-green-500">
									Image: {article.imageUrl}
								</div>
							)}

							{deleted.includes(i) && (
								<div className="absolute inset-0 z-1 bg-white/70 dark:bg-gray-900/70" />
							)}
						</div>
					))}
				</div>
			</div>
		</Dialog>
	)
}
